import { useEffect, type ReactNode } from 'react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: string;
  footer?: ReactNode;
  children: ReactNode;
  widthClassName?: string;
}

export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  footer,
  children,
  widthClassName = 'max-w-lg',
}: ModalProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-foreground-950/50 animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative z-10 w-full ${widthClassName} animate-modal-in overflow-hidden rounded-lg border border-background-200 bg-background-50`}
      >
        <header className="flex items-start gap-3 border-b border-background-200 px-5 py-4">
          {icon ? (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700 text-base">
              <i className={icon} />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-base font-semibold text-foreground-950">
              {title}
            </h3>
            {subtitle ? (
              <p className="mt-0.5 text-xs text-foreground-500">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-foreground-500 cursor-pointer transition-colors hover:bg-background-200 hover:text-foreground-900"
          >
            <i className="ri-close-line" />
          </button>
        </header>

        <div className="max-h-[65vh] overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <footer className="flex flex-col-reverse gap-2 border-t border-background-200 px-5 py-4 sm:flex-row sm:justify-end">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}